<template>
  <div class="form">
    <h1 class="extra-large-headline">{{ formTitle }}</h1>
    <p class="bodycopy">
      {{ formDescription }}
    </p>
    <form @submit.prevent>
      <component
        :is="loadDynamicFormPath"
        @payment-complete="handleSuccessRedirect"
        @payment-failed="handleFailureRedirect"
      />
    </form>
  </div>
</template>

<script setup>
import {
 ref, defineAsyncComponent, computed, onMounted,
} from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

import { useOrderFormStore } from '@/stores/forms/orderFormStore';
import {
} from '@/stores/forms/metamaskDepositFormStore';

import { capitaliseFirstChar } from '@/composables/helpers/useStringHelpers';
import useOrderEndpoints from '@/composables/api/useOrderEndpoints';
import usePaymentEndpoints from '@/composables/api/usePaymentEndpoints';
import useErrorHandling from '@/composables/api/useErrorHandling';
import usePaymentRedirect from '@/composables/payments/usePaymentRedirect';

const orderStore = useOrderFormStore();
const { handleErrorInSpaFlow } = useErrorHandling();
const { handleFailureRedirect, handleSuccessRedirect } = usePaymentRedirect();
const { getOrderId } = useOrderEndpoints();
const { getPaymentAuthorizeStatus } = usePaymentEndpoints();

const route = useRoute();
const form = ref({
  payment: orderStore.getPaymentProvider?.paymentType,
});
const { t } = useI18n();

const loadDynamicFormPath = computed(() => {
  const payment = capitaliseFirstChar(form.value.payment.toLowerCase());
  return defineAsyncComponent(() => import(/* @vite-ignore */ `./Payments/${payment}/${payment}Form.vue`));
});

const formTitle = computed(() => {
  // buy
  if (orderStore.isBuyMode || orderStore.isNFTMode) {
    const { payment } = route.params;
    switch (payment.toLowerCase()) {
      case 'primercc':
        return t('PaymentForm.title.primercc');
      default:
        return t('PaymentForm.label.paymentDetails');
    }
  }

  // sell
  return t('PaymentForm.label.depositDetails');
});

const paymentTypeCode = computed(() => orderStore.getPaymentObject?.pspTypeCode);

const formDescription = computed(() => {
  if (!orderStore.isBuyMode && !orderStore.isNFTMode) {
    return '';
  }
  return t('PaymentForm.label.instruction');
});

const proceedToOrderStatus = async () => {
  const orderId = await getOrderId();
  if (!orderId) {
    handleErrorInSpaFlow(4015, t('PlopenbankForm.error.getOrderId'));
    return;
  }

  handleSuccessRedirect(orderId);
};

const checkIfPaymentAlreadyProcessed = async () => {
  const status = JSON.parse(sessionStorage.getItem('payActionDone'));
  if (status) {
    proceedToOrderStatus();
  } else {
    // Get the value from SPA-BE
    const res = await getPaymentAuthorizeStatus();
    const authStatus = res?.data?.authorized;
    if (authStatus) {
      proceedToOrderStatus();
    }
  }
};

/**
 * Check if the PSP is eligible to check the status of the payment
 * @returns {boolean}
 *
 * Aim is to have this for all the PSPs, for now, releasing it in a controlled manner
 */
 const isPspEligibleToCheckStatus = (code) => {
  if (typeof code !== 'string') {
    console.warn('isPspEligibleToCheckStatus: code must be a string');
    return false;
  }
  const eligiblePSPs = ['PRIMERCC'];
  return eligiblePSPs.includes(code.toUpperCase());
};

onMounted(() => {
  if (isPspEligibleToCheckStatus(paymentTypeCode.value)) {
    checkIfPaymentAlreadyProcessed();
  }
});
</script>
<style lang="scss"></style>
